import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen,
  Settings,
  ChevronLeft,
  Plus,
  Sparkles,
  FileText,
  Library,
  LogOut,
  Download,
  HardDrive,
  Shield,
  Users,
  ScrollText,
  BarChart3,
  UsersRound,
  AlertTriangle,
  Calculator,
  Activity,
  MessageSquare,
} from 'lucide-react';
import AtlasDraftIcon from '@/assets/atlasdraft-icon.svg';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { useDevMode } from '@/hooks/useDevMode';
import { getPendingDraftStatic } from '@/hooks/useDraftProtection';
import { RateLimitIndicator } from '@/components/RateLimitIndicator';
import { DevTestingPanel } from '@/components/DevTestingPanel';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  collapsed: boolean;
}

function NavItem({ to, icon: Icon, label, collapsed }: NavItemProps) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  const navContent = (
    <NavLink to={to} className="block">
      <motion.div
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
          'hover:bg-sidebar-accent group',
          isActive && 'bg-sidebar-accent text-sidebar-accent-foreground'
        )}
        whileTap={{ scale: 0.98 }}
      >
        <Icon
          className={cn(
            'w-5 h-5 flex-shrink-0 transition-colors',
            isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground'
          )}
        />
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className={cn(
                'text-sm font-medium whitespace-nowrap overflow-hidden',
                isActive ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/80'
              )}
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{navContent}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return navContent;
}

export function AppSidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [hasPendingDraft, setHasPendingDraft] = useState(false);
  const { signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const { canShowDevPanel } = useDevMode();
  const navigate = useNavigate();

  // Persist collapsed state to localStorage (per-device)
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // Check for pending drafts periodically
  useEffect(() => {
    const checkPendingDraft = () => {
      const draft = getPendingDraftStatic();
      setHasPendingDraft(draft !== null);
    };

    checkPendingDraft();
    const interval = setInterval(checkPendingDraft, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <TooltipProvider delayDuration={0}>
      <motion.aside
        className={cn(
          'h-screen bg-sidebar border-r border-sidebar-border flex flex-col',
          'transition-all duration-300 ease-in-out'
        )}
        initial={false}
        animate={{ width: collapsed ? 72 : 260 }}
      >
      {/* Logo */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3 min-w-0 overflow-hidden">
          <div className="w-10 h-10 min-w-[40px] flex-shrink-0">
            <img src={AtlasDraftIcon} alt="AtlasDraft" className="w-full h-full" />
          </div>
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden flex-shrink-0"
              >
                <h1 className="font-logo font-semibold text-2xl text-sidebar-foreground whitespace-nowrap">
                  AtlasDraft
                </h1>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* New Project Button */}
      <div className="p-3">
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink to="/projects/new">
                <Button
                  variant="glow"
                  className="w-full justify-center gap-2 overflow-hidden"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                </Button>
              </NavLink>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              New Project
            </TooltipContent>
          </Tooltip>
        ) : (
          <NavLink to="/projects/new">
            <Button
              variant="glow"
              className="w-full justify-start gap-2 overflow-hidden"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <motion.span
                initial={{ opacity: 0, width: 0, marginLeft: -8 }}
                animate={{ opacity: 1, width: 'auto', marginLeft: 0 }}
                transition={{ duration: 0.2 }}
                className="whitespace-nowrap overflow-hidden"
              >
                New Project
              </motion.span>
            </Button>
          </NavLink>
        )}
      </div>

      {/* Pending Draft Indicator */}
      <AnimatePresence>
        {hasPendingDraft && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-3 pb-2"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400',
                      collapsed && 'justify-center px-2'
                    )}
                  >
                    <HardDrive className="w-4 h-4 flex-shrink-0 animate-pulse" />
                    {!collapsed && (
                      <span className="text-xs font-medium">Draft saved locally</span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>You have unsaved changes that will sync when online</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
        <NavItem to="/projects" icon={FolderOpen} label="My Projects" collapsed={collapsed} />
        <NavItem to="/research" icon={Library} label="Research Library" collapsed={collapsed} />
        
        <div className="pt-4 pb-2">
          <span 
            className={cn(
              "px-3 text-xs font-medium text-sidebar-foreground/40 uppercase tracking-wider transition-opacity duration-200",
              collapsed ? "opacity-0" : "opacity-100"
            )}
          >
            Tools
          </span>
        </div>
        
        <NavItem to="/ai-assistant" icon={Sparkles} label="AI Assistant" collapsed={collapsed} />
        <NavItem to="/templates" icon={FileText} label="Templates" collapsed={collapsed} />
        
        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              <span 
                className={cn(
                  "px-3 text-xs font-medium text-sidebar-foreground/40 uppercase tracking-wider transition-opacity duration-200",
                  collapsed ? "opacity-0" : "opacity-100"
                )}
              >
                Admin
              </span>
            </div>
            <NavItem to="/admin/settings" icon={Shield} label="Admin Settings" collapsed={collapsed} />
            <NavItem to="/admin/ai-prompts" icon={MessageSquare} label="AI Prompts" collapsed={collapsed} />
            <NavItem to="/admin/users" icon={Users} label="User Management" collapsed={collapsed} />
            <NavItem to="/admin/audit-logs" icon={ScrollText} label="Audit Logs" collapsed={collapsed} />
            <NavItem to="/admin/ai-analytics" icon={BarChart3} label="AI Analytics" collapsed={collapsed} />
            <NavItem to="/admin/user-analytics" icon={UsersRound} label="User Analytics" collapsed={collapsed} />
            <NavItem to="/admin/alerts" icon={AlertTriangle} label="Usage Alerts" collapsed={collapsed} />
            <NavItem to="/admin/pricing" icon={Calculator} label="Pricing & Margins" collapsed={collapsed} />
            <NavItem to="/admin/platform-health" icon={Activity} label="Platform Health" collapsed={collapsed} />
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-sidebar-border space-y-1">
        {/* Dev Testing Panel */}
        {canShowDevPanel && (
          <DevTestingPanel collapsed={collapsed} onExpandSidebar={() => setCollapsed(false)} />
        )}
        
        {/* Rate Limit Indicator */}
        <RateLimitIndicator collapsed={collapsed} />
        
        <NavItem to="/install" icon={Download} label="Install App" collapsed={collapsed} />
        <NavItem to="/settings" icon={Settings} label="Settings" collapsed={collapsed} />
        
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Log Out
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              transition={{ duration: 0.2 }}
              className="text-sm whitespace-nowrap overflow-hidden"
            >
              Log Out
            </motion.span>
          </button>
        )}
        
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors overflow-hidden"
            >
              <motion.div
                animate={{ rotate: collapsed ? 180 : 0 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="flex-shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.div>
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-sm whitespace-nowrap overflow-hidden"
                  >
                    Collapse
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={8}>
              Expand
            </TooltipContent>
          )}
        </Tooltip>
      </div>
      </motion.aside>
    </TooltipProvider>
  );
}
